import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import DOMPurify from "dompurify";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { TechArticle, TechArticles } from "../../interfaces";
import { API_URL } from "../../constants/urls";
import ArticleLink from "../ArticleLink/ArticleLink";
import "./Article.css";

function CommentForm({ articleId }: { articleId: string }) {
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setStatus("sending");
    try {
      await axios.post(
        `${API_URL}apis/nuhman/tech/comment`,
        {
          articleId: Number(articleId),
          name: values.get("name"),
          email: values.get("email"),
          subject: values.get("subject"),
          comment: values.get("comment"),
        },
        { timeout: 15000 },
      );
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
    }
  }
  return (
    <section className="reader-comments" aria-labelledby="comment-title">
      <p className="ed-eyebrow">Join the conversation</p>
      <h2 id="comment-title">What’s your perspective?</h2>
      <p>
        Share a question, an insight, or something you’ve learned. Comments are
        reviewed before publishing.
      </p>
      {status === "success" ? (
        <div className="reader-notice" role="status">
          Thank you. Your comment has been submitted for review.
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="reader-form-row">
            <label>
              Your name
              <input name="name" autoComplete="name" required maxLength={120} />
            </label>
            <label>
              Email address
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
              />
            </label>
          </div>
          <label>
            Subject
            <input name="subject" required maxLength={200} />
          </label>
          <label>
            Your comment
            <textarea name="comment" rows={5} required maxLength={5000} />
          </label>
          <p className="reader-form-note">
            Your email address will not be published.
          </p>
          {status === "error" && (
            <p role="alert" className="reader-error">
              Your comment couldn’t be sent. Your text is saved here — please
              try again.
            </p>
          )}
          <button
            type="submit"
            className="ed-button"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Sending…" : "Submit comment ↗"}
          </button>
        </form>
      )}
    </section>
  );
}

export default function Article() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const articleId = id || params.get("articleid") || "";
  const [article, setArticle] = useState<TechArticle>();
  const [related, setRelated] = useState<TechArticle[]>([]);
  const [status, setStatus] = useState<
    "loading" | "ready" | "missing" | "error"
  >("loading");
  const [retry, setRetry] = useState(0);
  const [shareMessage, setShareMessage] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setArticle(undefined);
    setRelated([]);
    setShareMessage("");
    window.scrollTo(0, 0);
    if (!/^\d+$/.test(articleId)) {
      setStatus("missing");
      return;
    }
    setStatus("loading");
    axios
      .get<TechArticle>(`${API_URL}apis/nuhman/tech/articles/${articleId}`, {
        signal: controller.signal,
        timeout: 15000,
      })
      .then((res) => {
        if (!res.data?.id) {
          setStatus("missing");
          return;
        }
        setArticle(res.data);
        setStatus("ready");
      })
      .catch((err) => {
        if (!axios.isCancel(err))
          setStatus(err.response?.status === 404 ? "missing" : "error");
      });
    return () => controller.abort();
  }, [articleId, retry]);
  useEffect(() => {
    if (!article) return;
    const controller = new AbortController();
    const query = new URLSearchParams({ size: "4", created: "1" });
    if (article.techCategory?.id)
      query.set("categoryId", String(article.techCategory.id));
    axios
      .get<TechArticles>(`${API_URL}apis/nuhman/tech/articles?${query}`, {
        signal: controller.signal,
        timeout: 15000,
      })
      .then((res) =>
        setRelated(
          (res.data.techArticles || [])
            .filter((item) => item.id !== article.id)
            .slice(0, 3),
        ),
      )
      .catch(() => {});
    return () => controller.abort();
  }, [article]);
  const content = useMemo(() => {
    const clean = DOMPurify.sanitize(article?.description || "", {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["form", "input", "button", "textarea", "select", "style"],
      FORBID_ATTR: ["style"],
    });
    const doc = new DOMParser().parseFromString(clean, "text/html");
    const fragmentIds = new Map<string, string>();
    doc.querySelectorAll("[id]").forEach((el, index) => {
      const originalId = el.id;
      el.id = `reader-anchor-${index}`;
      fragmentIds.set(originalId, el.id);
    });
    const headings = Array.from(doc.querySelectorAll("h2,h3")).map(
      (el, index) => {
        if (!el.id) el.id = `reader-section-${index}`;
        return { id: el.id, title: el.textContent || "", level: el.tagName };
      },
    );
    doc.querySelectorAll('a[href^="#"]').forEach((el) => {
      const originalId = el.getAttribute("href")!.slice(1);
      if (fragmentIds.has(originalId))
        el.setAttribute("href", `#${fragmentIds.get(originalId)}`);
    });
    doc
      .querySelectorAll('a[target="_blank"]')
      .forEach((el) => el.setAttribute("rel", "noopener noreferrer"));
    // Fragment links scroll within the article without replacing the app's hash route.
    const text = doc.body.textContent || "";
    return {
      html: doc.body.innerHTML,
      headings,
      minutes: Math.max(1, Math.ceil(text.trim().split(/\s+/).length / 220)),
    };
  }, [article]);
  const summary = useMemo(
    () =>
      new DOMParser().parseFromString(
        article?.shortDescription || article?.xshortDescription || "",
        "text/html",
      ).body.textContent || "",
    [article],
  );
  const tags = Array.from(
    new Set(
      `${article?.tags || ""},${article?.generatedTags || ""}`
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ).slice(0, 8);
  const date = article?.createdDateTime
    ? new Date(article.createdDateTime)
    : null;
  const validDate = date && !isNaN(date.getTime()) ? date : null;
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareMessage("Link copied");
    } catch {
      setShareMessage("Copy the URL from your address bar to share.");
    }
  }
  return (
    <div className="ed-wrap ed-page reader-page">
      <Helmet>
        <title>
          {article?.title ? `${article.title} — Nuhman` : "Article — Nuhman"}
        </title>
        <meta name="description" content={summary.slice(0, 180)} />
        <meta property="og:title" content={article?.title || "Nuhman"} />
        <meta property="og:description" content={summary.slice(0, 180)} />
      </Helmet>
      <nav className="reader-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Discover</Link>
        <span aria-hidden="true">/</span>
        {article?.techCategory?.id ? (
          <Link to={`/categories?catid=${article.techCategory.id}&aqry=true`}>
            {article.techCategory.name}
          </Link>
        ) : (
          <Link to="/latest">Articles</Link>
        )}
        <span aria-hidden="true">/</span>
        <span>Reading room</span>
      </nav>
      {status === "loading" ? (
        <div className="ed-loading" role="status">
          <span />
          Opening your next read…
        </div>
      ) : status === "missing" ? (
        <div className="ed-state">
          <h1>Article not found.</h1>
          <p>This article may have moved. There’s more to discover.</p>
          <Link className="ed-button" to="/latest">
            Explore the latest articles ↗
          </Link>
        </div>
      ) : status === "error" ? (
        <div className="ed-state" role="alert">
          <h1>We couldn’t open this article.</h1>
          <p>Please try again in a moment.</p>
          <button className="ed-button" onClick={() => setRetry((n) => n + 1)}>
            Try again ↻
          </button>
        </div>
      ) : (
        article && (
          <>
            <header className="reader-heading">
              <p className="ed-eyebrow">
                <span className="ed-dot" />
                {article.techCategory?.name || "Ideas & perspectives"}
              </p>
              <h1>{article.title}</h1>
              {summary && <p className="reader-deck">{summary}</p>}
              <div className="reader-meta">
                <div className="reader-byline">
                  <span className="reader-monogram" aria-hidden="true">
                    n.
                  </span>
                  <div>
                    <strong>Nuhman · The reading room</strong>
                    <div>
                      {validDate && (
                        <time dateTime={validDate.toISOString()}>
                          {validDate.toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </time>
                      )}
                      <span>{content.minutes} min read</span>
                    </div>
                  </div>
                </div>
                <button className="reader-share" onClick={copyLink}>
                  Copy article link ↗
                </button>
                <span className="reader-share-status" role="status">
                  {shareMessage}
                </span>
              </div>
            </header>
            <div className="reader-layout">
              <article className="reader-main">
                <div
                  className="reader-prose"
                  onClick={(event) => {
                    const link = (event.target as HTMLElement).closest("a");
                    if (link?.getAttribute("href")?.startsWith("#")) {
                      event.preventDefault();
                      const target = document.getElementById(
                        link.getAttribute("href")!.slice(1),
                      );
                      target?.scrollIntoView({
                        behavior: "auto",
                        block: "start",
                      });
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: content.html }}
                />
                {!content.html && (
                  <p className="reader-notice">
                    This article’s content is currently unavailable.
                  </p>
                )}
                {tags.length > 0 && (
                  <div className="reader-tags">
                    <p className="ed-eyebrow">Filed under</p>
                    <div>
                      {tags.map((tag) => (
                        <Link
                          key={tag}
                          to={`/tags?name=${encodeURIComponent(tag)}`}
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                <div className="reader-endnote">
                  <span aria-hidden="true">✳</span>
                  <p>
                    Good ideas are worth sharing.
                    <br />
                    <strong>Pass this one along.</strong>
                  </p>
                  <button className="reader-share" onClick={copyLink}>
                    Copy link ↗
                  </button>
                </div>
                <CommentForm key={articleId} articleId={articleId} />
              </article>
              <aside className="reader-sidebar">
                <div className="reader-sticky">
                  {content.headings.length > 0 && (
                    <nav aria-label="On this page" className="reader-toc">
                      <p className="ed-eyebrow">In this article</p>
                      {content.headings.map((heading, index) => (
                        <button
                          key={heading.id}
                          className={
                            heading.level === "H3" ? "reader-toc-sub" : ""
                          }
                          onClick={() => {
                            const el = document.getElementById(heading.id);
                            el?.setAttribute("tabindex", "-1");
                            el?.focus({ preventScroll: true });
                            el?.scrollIntoView({
                              behavior: "auto",
                              block: "start",
                            });
                          }}
                        >
                          <span>{String(index + 1).padStart(2, "0")}</span>
                          {heading.title}
                        </button>
                      ))}
                    </nav>
                  )}
                  <div className="reader-tool-note">
                    <span aria-hidden="true">{"{ }"}</span>
                    <p className="ed-eyebrow">From reading to making</p>
                    <h2>
                      Put your ideas
                      <br />
                      to work.
                    </h2>
                    <p>Handy tools for the things you build every day.</p>
                    <Link to="/smart-tools">Explore the toolkit ↗</Link>
                  </div>
                  <Link className="reader-back" to="/latest">
                    ← Back to all articles
                  </Link>
                </div>
              </aside>
            </div>
            {related.length > 0 && (
              <section className="reader-related">
                <div className="ed-section-heading">
                  <div>
                    <p className="ed-eyebrow">Keep the curiosity going</p>
                    <h2>Your next read</h2>
                  </div>
                  <Link
                    to={`/categories?catid=${article.techCategory?.id || ""}&aqry=true`}
                  >
                    More on this topic ↗
                  </Link>
                </div>
                <div className="ed-article-grid">
                  {related.map((item) => (
                    <article className="ed-article-card" key={item.id}>
                      <span className="ed-eyebrow">
                        {item.techCategory?.name}
                      </span>
                      <h3>
                        <ArticleLink
                          title={item.title || "Read article"}
                          id={item.id!}
                        />
                      </h3>
                      <span className="reader-related-arrow" aria-hidden="true">
                        ↗
                      </span>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </>
        )
      )}
    </div>
  );
}
